import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useOptionalShell } from './shell';
import { pageTypeOf, v2 } from './track';
import './tour.css';

/** One stop on a page tour. `target` matches an element's data-tour attribute. */
export interface TourStep {
  target: string;
  title: string;
  body: string;
}

interface TourApi {
  /** True when the current page has registered steps. */
  available: boolean;
  start: () => void;
  setSteps: (steps: TourStep[] | null) => void;
  /** Adds a step that follows every page's own (e.g. the chat button); returns its removal. */
  addSiteStep: (step: TourStep) => () => void;
}

const TourContext = createContext<TourApi>({ available: false, start: () => {}, setSteps: () => {}, addSiteStep: () => () => {} });

export const useTour = () => useContext(TourContext);

/** Pages call this to say what their tour covers. */
export function useTourSteps(steps: TourStep[]) {
  const { setSteps } = useTour();
  const key = steps.map((s) => s.target + s.title + s.body).join('|');
  useEffect(() => {
    setSteps(steps);
    return () => setSteps(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key captures the content
  }, [key, setSteps]);
}

/**
 * A step on every page's tour, after the page's own, for something that isn't part of any one page (the chat
 * button). Pass null to add nothing. Like every step it is skipped while its target isn't on screen.
 */
export function useSiteTourStep(step: TourStep | null) {
  const { addSiteStep } = useTour();
  const key = step ? step.target + step.title + step.body : '';
  useEffect(() => {
    if (!step) return undefined;
    return addSiteStep(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key captures the content
  }, [key, addSiteStep]);
}

const findTarget = (target: string): HTMLElement | null => {
  const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
  return el && el.getClientRects().length ? el : null;
};

/** Holds the current page's tour and draws it: a spotlight on each section with a short explanation. */
export function TourProvider({ children }: { children: ReactNode }) {
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  const [siteSteps, setSiteSteps] = useState<TourStep[]>([]);
  const [live, setLive] = useState<TourStep[] | null>(null);
  const [i, setI] = useState(0);
  const shell = useOptionalShell();

  const addSiteStep = useCallback((step: TourStep) => {
    setSiteSteps((current) => [...current.filter((s) => s.target !== step.target), step]);
    return () => setSiteSteps((current) => current.filter((s) => s !== step));
  }, []);

  const start = useCallback(() => {
    // the page's own sections, then the site-wide ones; skip any that aren't on screen right now
    const shown = [...(steps ?? []), ...siteSteps].filter((s) => findTarget(s.target));
    if (!shown.length) return;
    setLive(shown);
    setI(0);
  }, [steps, siteSteps]);

  // The help button belongs to pages with a tour; site-wide steps only ride along on those.
  const api = useMemo(() => ({ available: !!steps?.length, start, setSteps, addSiteStep }), [steps, start, addSiteStep]);
  return (
    <TourContext.Provider value={api}>
      {children}
      {live && (
        <TourOverlay
          steps={live}
          index={i}
          onIndex={setI}
          onClose={() => {
            // outside the router, so the page comes from the address bar
            shell?.track(v2('tour_end'), {
              pageType: pageTypeOf(window.location.pathname),
              uiVersion: 'new',
              step: i + 1,
              of: live.length,
              completed: i === live.length - 1,
            });
            setLive(null);
          }}
        />
      )}
    </TourContext.Provider>
  );
}

function TourOverlay({ steps, index, onIndex, onClose }: { steps: TourStep[]; index: number; onIndex: (n: number) => void; onClose: () => void }) {
  const step = steps[index];
  const [rect, setRect] = useState<DOMRect | null>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const last = index === steps.length - 1;

  const go = useCallback(
    (d: number) => {
      const n = index + d;
      if (n < 0) return;
      if (n >= steps.length) onClose();
      else onIndex(n);
    },
    [index, steps.length, onClose, onIndex],
  );

  useLayoutEffect(() => {
    const el = findTarget(step.target);
    if (!el) return;
    el.scrollIntoView({ block: 'center' });
    const measure = () => setRect(el.getBoundingClientRect());
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    nextRef.current?.focus({ preventScroll: true });
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step.target]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [go, onClose]);

  if (!rect) return null;
  const pad = 6;
  const cardW = Math.min(320, window.innerWidth - 24);
  const below = rect.bottom + 200 < window.innerHeight || rect.top < 200;
  const left = Math.max(12, Math.min(window.innerWidth - cardW - 12, rect.left));
  return createPortal(
    <div className="ui-tour">
      <div className="ui-tour-block" onClick={onClose} />
      <div
        className="ui-tour-hole"
        style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }}
      />
      <div
        className="ui-tour-card"
        role="dialog"
        aria-live="polite"
        aria-label={step.title}
        style={{ width: cardW, left, ...(below ? { top: rect.bottom + 14 } : { bottom: window.innerHeight - rect.top + 14 }) }}
      >
        <div className="n">
          {index + 1} of {steps.length}
        </div>
        <h4>{step.title}</h4>
        <p>{step.body}</p>
        <div className="b">
          <button type="button" className="q skip" onClick={onClose}>
            Skip
          </button>
          {index > 0 && (
            <button type="button" className="q" onClick={() => go(-1)}>
              Back
            </button>
          )}
          <button type="button" ref={nextRef} onClick={() => go(1)}>
            {last ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
